import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDeal } from "../lib/store";
import { offmarketApi } from "../lib/api";
import { csvCell } from "../lib/utils";
import { Card, Tile, ResearchModal, AddToCampaignModal, useOutreach, ComposeEmailModal, useCompose } from "../components/shared";
import { useDealFacts } from "../components/SequencePicker";
import { CriteriaPanel } from "../components/offmarket/CriteriaPanel";
import { ApolloSearchModal } from "../components/offmarket/ApolloSearchModal";
import { ApifyScrapeModal } from "../components/offmarket/ApifyScrapeModal";
import { EditTargetModal } from "../components/offmarket/EditTargetModal";
import { TargetRow, STAGES } from "../components/offmarket/TargetRow";
import { bodyWithOpener } from "../components/offmarket/FitNotes";
import { toast } from "sonner";
import { Upload, Globe, Database, Search, Share2, Sparkles, UserSearch, Download, Mail, Crosshair, MapPin } from "lucide-react";

const S = (v) => (Array.isArray(v) ? v.join(", ") : v == null ? "" : String(v).trim());
const num = (v) => { const n = parseFloat(String(v ?? "").replace(/[^0-9.]/g, "")); return isNaN(n) ? null : n; };
// "512 FM 108 South, Gonzales, TX 78629" -> "Gonzales, TX"
export const cityOf = (addr) => {
  const parts = S(addr).split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return S(addr);
  const st = parts[parts.length - 1].replace(/\s*\d{5}(-\d{4})?$/, "").replace(/\bUSA?\b/i, "").trim();
  const city = parts[parts.length - 2].replace(/^\d+\s+/, "");
  return [city, st].filter(Boolean).join(", ");
};

const parseCsv = (text) => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const delim = lines[0].includes("\t") ? "\t" : lines[0].includes(";") ? ";" : ",";
  const split = (l) => l.match(delim === "," ? /("([^"]|"")*"|[^,]*)(,|$)/g : null)?.map((c) => c.replace(/,$/, "").replace(/^"|"$/g, "").replace(/""/g, '"').trim()) || l.split(delim).map((c) => c.trim().replace(/^"|"$/g, ""));
  const hdr = split(lines[0]).map((h) => h.toLowerCase());
  const col = (...keys) => hdr.findIndex((h) => keys.some((k) => h.includes(k)));
  const i = {
    company: col("company", "business", "firm", "name"), website: col("website", "url", "domain"), phone: col("phone", "tel"), address: col("address", "street"),
    city: col("city", "town"), state: col("state", "province"), owner: col("owner", "contact", "principal"), email: col("email"), revenue: col("revenue", "sales"),
    employees: col("employee", "staff", "headcount"), founded: col("founded", "established", "year"), services: col("service", "description", "category", "niche"), type: col("type", "segment"), notes: col("note", "comment"),
  };
  const first = hdr.findIndex((h) => h === "first name" || h === "firstname" || h === "first");
  const last = hdr.findIndex((h) => h === "last name" || h === "lastname" || h === "last");
  return lines.slice(1).map((l) => {
    const c = split(l);
    const g = (k) => (i[k] >= 0 ? S(c[i[k]]) : "");
    const owner = first >= 0 ? `${S(c[first])} ${last >= 0 ? S(c[last]) : ""}`.trim() : g("owner");
    return { company: g("company"), website: g("website"), phone: g("phone"), address: g("address"), city: g("city"), state: g("state"), owner, email: g("email").split(/[;,\s]/)[0],
      revenue: num(g("revenue")), employees: num(g("employees")) != null ? Math.round(num(g("employees"))) : null, founded: num(g("founded")) != null ? Math.round(num(g("founded"))) : null,
      services: g("services"), type: g("type"), notes: g("notes"), source: "upload" };
  }).filter((x) => x.company);
};

const toCsv = (rows) => {
  const cols = ["company", "website", "phone", "address", "city", "state", "owner", "ownerTitle", "email", "linkedin", "type", "revenue", "employees", "founded", "services", "score", "rationale", "stage"];
  const esc = (v) => csvCell(v);
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
};

export default function OffMarket() {
  const { M, dealName, industry } = useDeal();
  const facts = useDealFacts();
  const dealKey = M.dealKey || "";
  const dName = dealName || M.dealName || "";
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, avgScore: 0, hot: 0, contacted: 0, withEmail: 0, withPhone: 0 });
  const [crit, setCrit] = useState({});
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("All");
  const [listType, setListType] = useState("All");
  const [types, setTypes] = useState([]);
  const [minScore, setMinScore] = useState(0);
  const [sel, setSel] = useState({});
  const [expanded, setExpanded] = useState("");
  const [busy, setBusy] = useState({});
  const [bulkBusy, setBulkBusy] = useState("");
  const [apolloOpen, setApolloOpen] = useState(false);
  const [apifyOpen, setApifyOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [apifyOk, setApifyOk] = useState(false);
  useEffect(() => { offmarketApi.apifyStatus().then((r) => setApifyOk(!!r.configured)).catch(() => {}); }, []);
  const [showCrit, setShowCrit] = useState(true);
  const fileRef = useRef(null);
  const outreach = useOutreach();
  const compose = useCompose();

  // Perplexity web discovery (reuses the shared research modal, kind "offmarket" routes to /offmarket/discover).
  const [webOpen, setWebOpen] = useState(false);
  const [webQ, setWebQ] = useState("");
  const [webLoc, setWebLoc] = useState("");
  const [webLoading, setWebLoading] = useState(false);
  const [webResult, setWebResult] = useState(null);

  const scoreFacts = useMemo(() => ({ ...facts, employees: M.employeeCount || "", services: M.industry || industry || "" }), [facts, M.employeeCount, M.industry, industry]);
  const critDefaults = useMemo(() => ({ niche: M.industry || industry || "", homeBase: cityOf(M.address), radiusMiles: 100 }), [M.industry, industry, M.address]);
  const homeCity = cityOf(crit.homeBase || M.address || "");

  const load = () => Promise.all([
    offmarketApi.list({ dealKey, q: q || undefined, stage, minScore, type: listType, limit: 500 }).then(setRows),
    offmarketApi.stats(dealKey).then(setStats),
  ]).catch(() => {});
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [dealKey, q, stage, minScore, listType]); // eslint-disable-line
  useEffect(() => { offmarketApi.types(dealKey).then((d) => setTypes(d.types || [])).catch(() => {}); }, [dealKey, rows.length]);

  const setRowBusy = (id, v) => setBusy((b) => ({ ...b, [id]: v }));
  const picked = rows.filter((r) => sel[r.id]);
  const toggleAll = () => { const all = rows.length && rows.every((r) => sel[r.id]); const n = {}; if (!all) rows.forEach((r) => { n[r.id] = true; }); setSel(n); };

  const saveMany = async (items, source) => {
    const res = await offmarketApi.bulk({ items: items.map((x) => ({ ...x, source })), dealKey, dealName: dName, autoScore: true, facts: scoreFacts });
    toast.success(`${res.added} target(s) added${res.skipped ? ` · ${res.skipped} duplicates skipped` : ""}`);
    load();
    if (res.job) watchJob(res.job, "Scoring new targets", (j, tid) => { toast.success(`${j.found} of ${j.total} new targets scored`, { id: tid }); load(); });
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const items = parseCsv(await file.text());
      if (!items.length) return toast.error("No rows with a company name found — check the header row");
      // Name the batch so each niche stays its own list (falls back to the CSV's own Type column).
      const label = (window.prompt("Name this list (niche / company type):", listType !== "All" ? listType : "") || "").trim();
      await saveMany(label ? items.map((x) => ({ ...x, type: x.type || label })) : items, "upload");
      if (label) setListType(label);
    } catch { toast.error("Could not parse that file — export it as CSV first"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const runWeb = async (more) => {
    setWebLoading(true);
    try {
      const exclude = more ? [...rows.map((r) => r.company), ...((webResult?.entities || []).map((e) => e.name))] : rows.map((r) => r.company);
      const r = await offmarketApi.discover({ query: webQ, location: webLoc, count: 30, exclude });
      setWebResult(more ? { entities: [...(webResult?.entities || []), ...(r.entities || [])], citations: [...(webResult?.citations || []), ...(r.citations || [])] } : r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Web search failed"); }
    finally { setWebLoading(false); }
  };
  const startWeb = () => { setWebQ(crit.niche || M.industry || industry || ""); setWebLoc(homeCity || "Texas"); setWebResult(null); setWebOpen(true); };
  const saveWeb = (e) => saveMany([{ company: S(e.name), website: S(e.website), phone: S(e.phone), address: S(e.address), city: S(e.city), state: S(e.state), owner: S(e.owner), services: S(e.services), employees: e.employees ?? null, founded: e.founded ?? null, revenue: e.revenue ?? null, reviews: S(e.reviews), notes: S(e.note) }], "web");
  const describeWeb = (e) => (
    <div style={{ fontSize: 12 }}>
      <div><b>{e.name}</b>{e.reviews ? <span className="tag" style={{ marginLeft: 6, fontSize: 9 }}>{e.reviews}</span> : null}</div>
      {e.services && <div style={{ color: "var(--text2)", marginTop: 2 }}>{e.services}</div>}
      <div style={{ color: "var(--text3)", marginTop: 2, fontSize: 11 }}>{[[e.city, e.state].filter(Boolean).join(", "), e.phone, e.owner && `Owner: ${e.owner}`, e.founded && `est. ${e.founded}`].filter(Boolean).join(" · ")}</div>
      {e.note && <div style={{ color: "var(--lime)", marginTop: 2, fontSize: 11 }}>{e.note}</div>}
    </div>
  );

  // Long bulk work runs server-side; poll the job so the proxy never times out.
  const watchJob = (job, label, onDone) => new Promise((resolve) => {
    const tid = toast.loading(`${label} 0/${job.total}…`);
    const t = setInterval(async () => {
      try {
        const j = await offmarketApi.job(job.id);
        toast.loading(`${label} ${j.done}/${j.total}…`, { id: tid });
        load();
        if (j.status !== "running") {
          clearInterval(t);
          if (j.status === "interrupted") toast.error(`${label} stopped when the server restarted — run it again`, { id: tid });
          else onDone(j, tid);
          resolve(j);
        }
      } catch { clearInterval(t); toast.error(`${label} lost contact with the server`, { id: tid }); resolve(null); }
    }, 2500);
  });

  const scoreIds = async (ids) => {
    if (!ids.length) return toast.error("Select at least one target");
    setBulkBusy("score"); ids.forEach((id) => setRowBusy(id, "score"));
    try {
      const job = await offmarketApi.jobScore({ ids, dealKey, facts: scoreFacts });
      await watchJob(job, `Scoring against ${dName || "the platform"}`, (j, tid) => {
        if (j.status === "done") toast.success(`${j.found} of ${j.total} scored`, { id: tid }); else toast.error(j.error || "Scoring failed", { id: tid });
        load();
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Scoring failed"); }
    finally { setBulkBusy(""); ids.forEach((id) => setRowBusy(id, "")); }
  };

  const enrichOne = async (t) => {
    setRowBusy(t.id, "enrich");
    const tid = toast.loading(`Finding the owner of ${t.company}…`);
    try {
      const r = await offmarketApi.enrich(t.id);
      if (!r.found) toast.error("No owner found publicly — try the website or call the main line", { id: tid });
      else toast.success(`${r.target.owner || "Owner"}${r.target.ownerTitle ? `, ${r.target.ownerTitle}` : ""}${r.target.email ? ` · ${r.target.email}` : ""}`, { id: tid });
      setRows((rs) => rs.map((x) => (x.id === t.id ? { ...x, ...r.target } : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Enrichment failed", { id: tid }); }
    finally { setRowBusy(t.id, ""); }
  };
  const enrichMany = async () => {
    if (!picked.length) return toast.error("Select at least one target");
    setBulkBusy("enrich");
    try {
      const job = await offmarketApi.enrichMany(picked.map((p) => p.id));
      await watchJob(job, "Finding owners", (j, tid) => {
        if (j.status === "done") toast.success(`${j.found} owner(s) found of ${j.total}`, { id: tid }); else toast.error(j.error || "Enrichment failed", { id: tid });
        load();
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Enrichment failed"); }
    finally { setBulkBusy(""); }
  };

  const setStageFor = async (t, s) => {
    try { const r = await offmarketApi.update(t.id, { stage: s }); setRows((rs) => rs.map((x) => (x.id === t.id ? r : x))); offmarketApi.stats(dealKey).then(setStats); }
    catch { toast.error("Could not update the stage"); }
  };
  const del = async (t) => { await offmarketApi.remove(t.id); setRows((rs) => rs.filter((x) => x.id !== t.id)); offmarketApi.stats(dealKey).then(setStats); };

  const toContact = (t) => ({ name: t.owner || "", email: t.email, company: t.company, title: t.ownerTitle || "Owner", phone: t.phone, website: t.website, notes: [t.services, t.city, t.rationale].filter(Boolean).join(" · ") });
  const addToCampaign = () => {
    const list = picked.filter((t) => t.email);
    if (!list.length) return toast.error("Selected targets need an owner email — run “Find owners” first");
    outreach.start(list.map(toContact), `${dName || "Off-market"} — seller outreach`);
  };
  const openCompose = (one, opener = "") => {
    const list = (one ? [one] : picked).filter((t) => t.email);
    if (!list.length) return toast.error("No owner email on the selected targets yet");
    const t = list[0];
    compose.start({
      title: "Email business owner(s)",
      recipients: list.map((x) => ({ name: x.owner || x.company, email: x.email })),
      subject: `${t.company} — a quick, confidential question`,
      body: bodyWithOpener(`Hi ${(t.owner || "").split(" ")[0] || "there"},\n\nI'm with XYZ Ventures. We own ${dName || "a local business"}, a ${(M.industry || industry || "services").toLowerCase()} company in ${M.address || "the area"}, and we're looking to partner with one or two more well-run companies nearby.\n\n${t.company} kept coming up. I have no idea whether you've ever thought about selling or bringing on a partner, but if the timing is ever right I'd like to be your first call.\n\nWould a confidential 15-minute conversation be worth it? No brokers, no pressure — owner to owner.\n\nBest,\nXYZ Ventures`, opener),
      kind: "offmarket", dealName: dName, M,
      vars: [["Deal", dName], ["Target", t.company], ["Owner", t.owner || "—"], ["Score", t.score ?? "—"]],
      draftEndpoint: "/ai/seller-outreach",
      draftPayload: { deal: scoreFacts, target: { company: t.company, owner: t.owner, services: t.services, city: t.city, state: t.state, reviews: t.reviews, founded: t.founded, employees: t.employees, rationale: t.rationale } },
    });
    if (one) list.forEach((x) => offmarketApi.activity(x.id, { kind: "email", note: opener ? "Started seller email from AI opening line" : "Opened seller email composer" }).catch(() => {}));
  };
  const exportCsv = () => {
    const blob = new Blob([toCsv(picked.length ? picked : rows)], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${(dName || "offmarket").replace(/\s+/g, "_")}_targets.csv`; a.click();
  };

  return (
    <div className="page om" data-testid="page-offmarket">
      <div className="page-head">
        <div>
          <div className="page-title">Off-Market Targets</div>
          <div className="page-sub">Build a list of businesses that aren't for sale, score each one as a bolt-on to {dName || "your platform"}, find the owner, and ask the question.</div>
        </div>
        <div className="ub-head-acts">
          <button className="btn" onClick={() => setShowCrit((s) => !s)} data-testid="om-toggle-crit"><Crosshair size={13} />{showCrit ? "Hide fit rules" : "Fit rules"}</button>
          <button className="btn" onClick={startWeb} data-testid="om-find-web"><Globe size={13} />Search the web (AI)</button>
          <button className="btn" onClick={() => setApifyOpen(true)} title={apifyOk ? "Scrape Google Maps via Apify" : "Add APIFY_API_TOKEN to enable"} data-testid="om-find-apify"><MapPin size={13} />Google Maps scrape</button>
          <button className="btn" onClick={() => setApolloOpen(true)} data-testid="om-find-apollo"><Database size={13} />Apollo company search</button>
          <button className="btn btn-primary" onClick={() => fileRef.current?.click()} data-testid="om-upload"><Upload size={13} />Upload list</button>
          <input ref={fileRef} type="file" accept=".csv,.tsv,text/csv,text/tab-separated-values" style={{ display: "none" }} onChange={onFile} data-testid="om-file" />
        </div>
      </div>

      <div className="om-tiles">
        <Tile label="Targets" value={stats.total} sub={`${stats.scored || 0} scored`} testid="om-kpi-total" small />
        <Tile label="Avg fit score" value={stats.avgScore || "–"} sub="0–100 bolt-on fit" tone={stats.avgScore >= 65 ? "go" : "amber"} testid="om-kpi-avg" small />
        <Tile label="Strong bolt-ons" value={stats.hot} sub="score ≥ 75" tone="go" testid="om-kpi-hot" small />
        <Tile label="Owners reachable" value={stats.withEmail} sub={`${stats.withPhone} with phone`} testid="om-kpi-email" small />
        <Tile label="In conversation" value={stats.contacted} sub="past “New”" testid="om-kpi-contacted" small />
      </div>

      {showCrit && <CriteriaPanel dealKey={dealKey} defaults={critDefaults} onSaved={setCrit} />}

      <Card title={`Target list — ${rows.length} shown`} className="table-scroll" style={{ marginTop: 12 }} testid="om-list"
        right={<div className="om-toolbar">
          <div style={{ position: "relative" }}>
            <Search size={12} style={{ position: "absolute", left: 7, top: 8, color: "var(--text3)" }} />
            <input className="inp inp-sm" style={{ paddingLeft: 23, width: 190 }} placeholder="Search company, owner…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="om-search" />
          </div>
          <select className="inp inp-sm" style={{ width: 132 }} value={listType} onChange={(e) => setListType(e.target.value)} data-testid="om-type-filter">
            <option value="All">All lists</option>
            {types.map((t) => <option key={t.type} value={t.type}>{t.type} ({t.count})</option>)}
          </select>
          <select className="inp inp-sm" style={{ width: 92 }} value={stage} onChange={(e) => setStage(e.target.value)} data-testid="om-stage-filter"><option>All</option>{STAGES.map((s) => <option key={s}>{s}</option>)}</select>
          <select className="inp inp-sm" style={{ width: 104 }} value={minScore} onChange={(e) => setMinScore(Number(e.target.value))} data-testid="om-min-score">
            <option value={0}>Any score</option><option value={75}>≥ 75</option><option value={55}>≥ 55</option>
          </select>
          <button className="btn btn-sm" onClick={() => scoreIds(picked.map((p) => p.id))} disabled={!picked.length || bulkBusy === "score"} title="Score selected" data-testid="om-score-sel"><Sparkles size={12} />Score{picked.length ? ` (${picked.length})` : ""}</button>
          <button className="btn btn-sm" onClick={enrichMany} disabled={!picked.length || bulkBusy === "enrich"} title="Find owners for selected" data-testid="om-enrich-sel"><UserSearch size={12} />Owners</button>
          <button className={`btn btn-sm ${picked.length ? "btn-primary" : ""}`} onClick={addToCampaign} disabled={!picked.length} title="Add selected to a campaign" data-testid="om-campaign"><Share2 size={12} />Campaign</button>
          <button className="btn btn-sm" onClick={() => openCompose(null)} disabled={!picked.length} title="Email selected" data-testid="om-compose"><Mail size={12} /></button>
          <button className="btn btn-sm" onClick={exportCsv} disabled={!rows.length} title="Export CSV" data-testid="om-export"><Download size={12} /></button>
        </div>}>
        <table className="tbl om-tbl" style={{ minWidth: 1120 }} data-testid="om-table">
          <thead><tr>
            <th style={{ width: 28 }}><input type="checkbox" checked={rows.length > 0 && rows.every((r) => sel[r.id])} onChange={toggleAll} data-testid="om-all" /></th>
            <th style={{ width: 52 }}>Fit</th><th>Company</th><th>Owner</th><th>Phone / email</th><th>Location</th><th>List</th><th style={{ minWidth: 112 }}>Stage</th><th></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={9} className="om-empty" data-testid="om-empty">
                <b>No targets yet.</b> Upload a CSV (Company, Website, Phone, City, Owner, Email, Type, Revenue, Employees), search the web, or pull companies from Apollo. Everything you add is scored automatically against the fit rules above.
              </td></tr>
            ) : rows.map((t) => (
              <TargetRow key={t.id} t={t} checked={sel[t.id]} expanded={expanded === t.id} busy={busy[t.id]}
                onCheck={() => setSel((s) => ({ ...s, [t.id]: !s[t.id] }))} onExpand={() => setExpanded(expanded === t.id ? "" : t.id)}
                onStage={(s) => setStageFor(t, s)} onEnrich={() => enrichOne(t)} onScore={() => scoreIds([t.id])} onEmail={(line) => openCompose(t, typeof line === "string" ? line : "")} onEdit={() => setEditing(t)} onDelete={() => del(t)} />
            ))}
          </tbody>
        </table>
      </Card>

      <ResearchModal open={webOpen} onClose={() => setWebOpen(false)} title="Find off-market businesses — live web search"
        subtitle="Perplexity scans Google Maps, Yelp, Yellow Pages, BBB and company sites for real, independently owned businesses in this niche and area. Save adds them as targets and scores them."
        kind="offmarket" query={webQ} setQuery={setWebQ} location={webLoc} setLocation={setWebLoc}
        run={() => runWeb(false)} findMore={() => runWeb(true)} loading={webLoading} result={webResult} describe={describeWeb} onSave={saveWeb} />
      <ApolloSearchModal open={apolloOpen} onClose={() => setApolloOpen(false)} defaults={{ keywords: (crit.niche || M.industry || industry || "").split(/[&/,]/)[0].trim(), location: homeCity }}
        onSave={(list) => saveMany(list.map((c) => ({ ...c, apolloId: undefined })), "apollo")} />
      <ApifyScrapeModal open={apifyOpen} onClose={() => setApifyOpen(false)} configured={apifyOk} defaults={{ query: (crit.niche || M.industry || industry || "").split(/[&/,]/)[0].trim(), location: homeCity ? `${homeCity}, USA` : "" }}
        onSave={(list) => saveMany(list.map((p) => ({ company: p.name, website: p.website, phone: p.phone, address: p.address, city: p.city, state: p.state, services: p.services, reviews: p.reviews, notes: [p.note, p.mapsUrl].filter(Boolean).join(" · ") })), "google-maps")} />
      <EditTargetModal target={editing} onClose={() => setEditing(null)} onSaved={(r) => { setRows((rs) => rs.map((x) => (x.id === r.id ? r : x))); offmarketApi.stats(dealKey).then(setStats); }} />
      <AddToCampaignModal open={outreach.open} onClose={() => outreach.setOpen(false)} contacts={outreach.contacts} defaultGroupName={outreach.defaultName} source="offmarket" dealKey={dealKey} dealName={dName} />
      <ComposeEmailModal open={compose.open} onClose={() => compose.setOpen(false)} config={compose.config} />
    </div>
  );
}
